import { DETECT_WIDTH, detectPage, toGray } from './detect.js';
import { fullFrame, insetQuad, scaleQuad, type Quad } from './geometry.js';

/**
 * SCAN-005 — what a scan holds on to between the shutter and the PDF.
 *
 * The shape of this file is set by one number. A frame off a modern phone
 * camera is around four megapixels, an uncompressed bitmap of it is thirty-odd
 * megabytes, and a scan is as long as the person holding the phone wants it to
 * be — so keeping the frames as they arrive is a gigabyte by page thirty, on a
 * device that will close the browser tab rather than allow it.
 *
 * So a photographed page is kept the way a camera keeps one: shrunk to a size a
 * page is still legible at, and compressed. It is decoded again only twice —
 * once for the thumbnail in the review grid, and once at full size for the PDF
 * — and never more than one at a time.
 */

/** The longest side a kept photograph may have.
 *
 *  An A4 page across 2400 pixels is 200 dots per inch, which is the low end of
 *  what optical character recognition is reliable at and comfortably past what
 *  a person reading the page on a screen can tell from more. Above it the
 *  memory doubles and nothing else improves. */
const KEEP_MAX = 2400;

/** Thumbnails for the review grid. Big enough to see whether the crop caught
 *  the whole page, small enough that a stack of them redraws within a tap. */
export const THUMB_MAX = 520;

/** The page itself, in the finished PDF. */
export const PAGE_MAX = 2200;

export interface Photo {
  blob: Blob;
  width: number;
  height: number;
}

const naturalWidth = (source: ImageBitmap | HTMLImageElement): number =>
  source instanceof HTMLImageElement ? source.naturalWidth : source.width;
const naturalHeight = (source: ImageBitmap | HTMLImageElement): number =>
  source instanceof HTMLImageElement ? source.naturalHeight : source.height;

function encode(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('scan: the photograph would not encode'))),
      type,
      quality,
    );
  });
}

/**
 * A frame from the camera, put away.
 *
 * Quality 0.92 rather than the usual 0.8, because this is not the picture
 * anybody sees: it is the negative every later version is developed from, and
 * the compression artefacts of a first pass come back magnified by the contrast
 * the scan filter opens up. The saving is not worth it at either end — this is
 * half a megabyte a page either way.
 */
export async function keepPhoto(frame: ImageBitmap | HTMLImageElement): Promise<Photo> {
  const sourceWidth = naturalWidth(frame);
  const sourceHeight = naturalHeight(frame);
  const scale = Math.min(1, KEEP_MAX / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('scan: no canvas');
  context.drawImage(frame as CanvasImageSource, 0, 0, width, height);

  const blob = await encode(canvas, 'image/jpeg', 0.92);
  if (frame instanceof ImageBitmap) frame.close();
  return { blob, width, height };
}

/**
 * A kept photograph, decoded back into something the renderer can upload.
 *
 * `createImageBitmap` is the direct route and is what runs; the element is
 * there for Safari versions that have the function but refuse a blob, where the
 * alternative is a scanner that silently produces blank pages.
 */
export async function decodePhoto(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob);
    } catch {
      // Fall through to the element.
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('scan: the photograph would not decode'));
      image.src = url;
    });
  } finally {
    // Revoked once loaded: the decoded pixels belong to the element now, and a
    // scan of twenty pages leaks twenty of these otherwise.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * A detected outline, as something to cut along.
 *
 * The detector reports where it believes the page's edges are, which is the
 * honest answer and the one its own tests hold it to. A crop wants something
 * slightly different — to be certain it is inside the paper — and the two are
 * separated here so that a corner a person has placed by hand is cut exactly
 * where they put it, and only a guess gets the margin of doubt.
 */
export const cropFrom = (quad: Quad): Quad => insetQuad(quad);

/**
 * Where the page is in a still photograph.
 *
 * The same detector the viewfinder runs, on the main thread rather than in the
 * worker — this happens once per picture rather than fourteen times a second,
 * and a worker's whole justification is the frame it would otherwise cost.
 *
 * Falls back to the whole frame rather than to nothing. A photograph picked out
 * of the gallery is often already a scan, cropped to its own edges, and there
 * are then no page edges left inside it to find; treating that as a failure
 * would refuse to import the one kind of image that needs no work at all.
 */
export function findPage(source: ImageBitmap | HTMLImageElement): Quad {
  const width = naturalWidth(source);
  const height = naturalHeight(source);
  const small = Math.min(DETECT_WIDTH, width);
  const tall = Math.max(1, Math.round((height * small) / width));

  const canvas = document.createElement('canvas');
  canvas.width = small;
  canvas.height = tall;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return fullFrame(width, height);
  context.drawImage(source as CanvasImageSource, 0, 0, small, tall);

  const found = detectPage(toGray(context.getImageData(0, 0, small, tall).data), small, tall);
  return found ? cropFrom(scaleQuad(found, width / small, height / tall)) : fullFrame(width, height);
}
